/**
 * TrainingLaunchDisclosureProvider (K108-R Batch 1)
 *
 * Mounted once (alongside the durable consent provider). Shows a PER-LAUNCH
 * monitoring disclosure EVERY time a monitored training launch is requested, and
 * only runs the launch after the user clicks "Agree & continue".
 *
 * Popup-safety: `proceed` is invoked SYNCHRONOUSLY inside the "Agree & continue"
 * click (a fresh user gesture). It runs the existing gated launch — the durable
 * consent gate's cached path launches synchronously (window.open / Companion
 * open in-gesture), and the uncached path defers to the consent modal, whose own
 * placeholder keeps Safari popup-safe. This provider is deliberately SEPARATE
 * from `TrainingMonitoringConsentProvider` so the durable-consent contract (and
 * its tests) is unchanged.
 *
 * Not a legal loophole: no "don't show again"; the disclosure is always shown.
 */
import { createContext, useCallback, useContext, useState, type ReactNode } from 'react';
import { TrainingLaunchDisclosureModal } from '@/components/training/TrainingLaunchDisclosureModal';
import { TrainingLaunchFallbackDialog } from '@/components/training/TrainingLaunchFallbackDialog';

interface DisclosureValue {
  /**
   * Show the per-launch disclosure, then run `proceed` on "Agree & continue"
   * (a user gesture). "Cancel" / dismiss does NOT run `proceed`.
   */
  requestLaunch: (proceed: () => void) => void;
}

const TrainingLaunchDisclosureContext = createContext<DisclosureValue | null>(null);

export function useTrainingLaunchDisclosure(): DisclosureValue | null {
  return useContext(TrainingLaunchDisclosureContext);
}

export function TrainingLaunchDisclosureProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<{ open: boolean; proceed: (() => void) | null }>({
    open: false,
    proceed: null,
  });

  const requestLaunch = useCallback((proceed: () => void) => {
    // Always show the disclosure — every launch, no caching, no bypass.
    setState({ open: true, proceed });
  }, []);

  const onAgree = useCallback(() => {
    // Run the launch from THIS click gesture (popup-safe), then close.
    const proceed = state.proceed;
    setState({ open: false, proceed: null });
    proceed?.();
  }, [state.proceed]);

  const onCancel = useCallback(() => {
    // Dismiss without launching.
    setState({ open: false, proceed: null });
  }, []);

  return (
    <TrainingLaunchDisclosureContext.Provider value={{ requestLaunch }}>
      {children}
      <TrainingLaunchDisclosureModal
        open={state.open}
        onOpenChange={(next) => { if (!next) onCancel(); }}
        onAgree={onAgree}
        onCancel={onCancel}
      />
      {/* Safari/popup-block escape hatch — "Open training directly" (plain anchor). */}
      <TrainingLaunchFallbackDialog />
    </TrainingLaunchDisclosureContext.Provider>
  );
}
